import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const priorityColor = (p) => ({
  P1: "bg-rag-red text-background border-rag-red",
  P2: "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/40",
  P3: "bg-muted text-muted-foreground border-border",
  P4: "bg-muted text-muted-foreground border-border",
}[p] || "");

export const statusColor = (s) => ({
  open: "bg-muted text-muted-foreground border-border",
  in_progress: "bg-muted text-muted-foreground border-border",
  on_hold: "bg-muted text-muted-foreground border-border",
  waiting_vendor: "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/40",
  waiting_requester: "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/40",
  approval_pending: "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/40",
  resolved: "bg-rag-green-bg text-rag-green-fg border-rag-green/40",
  closed: "bg-rag-green-bg text-rag-green-fg border-rag-green/40",
  closed_spam: "bg-muted text-muted-foreground border-border",
}[s] || "");

export const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "on_hold", label: "On Hold" },
  { value: "waiting_vendor", label: "Waiting on Vendor" },
  { value: "waiting_requester", label: "Waiting on Requester" },
  { value: "approval_pending", label: "Approval Pending" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
  { value: "closed_spam", label: "Closed-Spam" },
];

// Terminal statuses (SLA clock stopped; treated as closed in the UI).
export const CLOSED_STATUSES = ["resolved", "closed", "closed_spam"];
export const PAUSED_STATUSES = ["on_hold", "waiting_vendor", "waiting_requester", "approval_pending"];
export const statusLabel = (s) => (STATUS_OPTIONS.find(o => o.value === s)?.label || (s || "").replace(/_/g, " "));

export const humanTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// SOC module renders all absolute timestamps in IST (Asia/Kolkata) regardless of the viewer's browser zone.
export const IST_TZ = "Asia/Kolkata";
export const fmtIST = (iso, { date = true, time = true, seconds = false } = {}) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const opts = { timeZone: IST_TZ };
  if (date) Object.assign(opts, { day: "2-digit", month: "short", year: "numeric" });
  if (time) Object.assign(opts, { hour: "2-digit", minute: "2-digit", hour12: false, ...(seconds ? { second: "2-digit" } : {}) });
  return d.toLocaleString("en-IN", opts) + (time ? " IST" : "");
};
export const fmtISTDate = (iso) => fmtIST(iso, { time: false });

export const formatDue = (iso) => fmtIST(iso);

export const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  const abs = Math.abs(seconds);
  const sign = seconds < 0 ? "-" : "";
  if (abs < 60) return `${sign}${Math.floor(abs)}s`;
  if (abs < 3600) return `${sign}${Math.floor(abs / 60)}m`;
  if (abs < 86400) return `${sign}${Math.floor(abs / 3600)}h ${Math.floor((abs % 3600) / 60)}m`;
  return `${sign}${Math.floor(abs / 86400)}d ${Math.floor((abs % 86400) / 3600)}h`;
};

// Business-hours duration: always flat hours (never rolls into calendar days),
// so "12 biz hours" reads as "12h", and 28 biz hours reads as "28h 0m" (not "1d 4h").
export const formatBizHours = (seconds) => {
  if (seconds == null) return "—";
  const abs = Math.abs(seconds);
  const sign = seconds < 0 ? "-" : "";
  if (abs < 60) return `${sign}${Math.floor(abs)}s`;
  if (abs < 3600) return `${sign}${Math.floor(abs / 60)}m`;
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  return m ? `${sign}${h}h ${m}m` : `${sign}${h}h`;
};

export const timeUntil = (iso) => {
  if (!iso) return { text: "—", breach: false };
  const d = new Date(iso).getTime();
  const diff = (d - Date.now()) / 1000;
  const abs = Math.abs(diff);
  const sign = diff < 0 ? "-" : "";
  let text;
  if (abs < 60) text = `${sign}${Math.floor(abs)}s`;
  else if (abs < 3600) text = `${sign}${Math.floor(abs / 60)}m`;
  else if (abs < 86400) text = `${sign}${Math.floor(abs / 3600)}h ${Math.floor((abs % 3600) / 60)}m`;
  else text = `${sign}${Math.floor(abs / 86400)}d ${Math.floor((abs % 86400) / 3600)}h`;
  return { text, breach: diff < 0 };
};

// ==================== SOC ====================
export const socSeverityColor = (s) => ({
  Critical: "bg-rag-red text-background border-rag-red",
  High: "bg-rag-red-bg text-rag-red-fg border-rag-red/30",
  Medium: "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/40",
  Low: "bg-muted text-muted-foreground border-border",
}[s] || "");

export const socStatusColor = (s) => ({
  new: "bg-muted text-muted-foreground border-border",
  triage: "bg-muted text-muted-foreground border-border",
  investigating: "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/40",
  containment: "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/40",
  eradication_recovery: "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/40",
  resolved: "bg-rag-green-bg text-rag-green-fg border-rag-green/40",
  closed: "bg-rag-green-bg text-rag-green-fg border-rag-green/40",
}[s] || "");

export const SOC_STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "triage", label: "Triage" },
  { value: "investigating", label: "Investigating" },
  { value: "containment", label: "Containment" },
  { value: "eradication_recovery", label: "Eradication & Recovery" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];
export const SOC_SEVERITIES = ["Critical", "High", "Medium", "Low"];
export const SOC_SOURCES = ["SIEM", "EDR", "Firewall/IDS", "Threat Intel", "User Report", "Email", "Manual"];
export const SOC_CLASSIFICATIONS = ["Undetermined", "True Positive", "False Positive", "Benign"];
export const socStatusLabel = (s) => (SOC_STATUS_OPTIONS.find(o => o.value === s)?.label || (s || "").replace(/_/g, " "));
export const TLP_OPTIONS = ["RED", "AMBER", "GREEN", "CLEAR"];
export const PAP_OPTIONS = ["RED", "AMBER", "GREEN", "CLEAR"];
export const SOC_IOC_TYPES = ["ip", "domain", "url", "hash", "email", "filename", "username"];
export const SOC_TASK_STATUS_OPTIONS = [
  { value: "todo", label: "To Do" },
  { value: "in_progress", label: "In Progress" },
  { value: "done", label: "Done" },
];
// Allowed status transitions (mirrors backend STATUS_TRANSITIONS). Current status is always included.
export const SOC_STATUS_TRANSITIONS = {
  new: ["triage", "investigating", "closed"],
  triage: ["new", "investigating", "closed"],
  investigating: ["triage", "containment", "resolved", "closed"],
  containment: ["investigating", "eradication_recovery", "resolved", "closed"],
  eradication_recovery: ["containment", "resolved", "closed"],
  resolved: ["investigating", "closed"],
  closed: ["investigating"],
};
export const socAllowedNextStatuses = (current) => {
  const next = SOC_STATUS_TRANSITIONS[current] || [];
  return [current, ...next.filter((s) => s !== current)];
};
export const tlpColor = (t) => ({
  RED: "bg-rag-red text-background border-rag-red",
  AMBER: "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/50",
  GREEN: "bg-rag-green-bg text-rag-green-fg border-rag-green/50",
  CLEAR: "bg-muted text-muted-foreground border-border",
}[t] || "");
export const papColor = tlpColor;

